import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Button, ButtonProps, buttonVariants } from '@/components/button'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={twMerge(
      'relative mx-auto flex w-full justify-center bg-white px-8 py-4',
      className,
    )}
    {...props}
  />
)
Pagination.displayName = 'Pagination'

type PaginationCurrentPageProps = {
  currentPage: number
  itemsPerPage: number
  totalOfItems: number
} & React.ComponentProps<'div'>

const PaginationCurrentPage = ({
  className,
  currentPage,
  itemsPerPage,
  totalOfItems,
}: PaginationCurrentPageProps) => {
  const calcListLength = itemsPerPage * currentPage
  const currentListLength =
    calcListLength >= totalOfItems ? totalOfItems : calcListLength

  return (
    <div
      className={twMerge(
        'flex max-w-16 items-center text-center sm:max-w-none lg:absolute lg:left-8 lg:top-1/2 lg:m-0 lg:-translate-y-1/2 lg:text-sm 2xl:text-base',
        className,
      )}
    >
      {String(currentListLength)} - {totalOfItems}
    </div>
  )
}
PaginationCurrentPage.displayName = 'PaginationCurrentPage'

type PaginationItemsPerPageProps = {
  itemsPerPage: string
  handleItemsPerPage: (value: string) => void
  isDisabled?: boolean
} & React.ComponentProps<'div'>

const PaginationItemsPerPage = ({
  itemsPerPage,
  handleItemsPerPage,
  className,
  isDisabled = false,
}: PaginationItemsPerPageProps) => (
  <div
    className={twMerge(
      'order-1 ml-2 flex items-center gap-4 lg:absolute lg:left-32 lg:right-5 lg:ml-0',
      className,
    )}
  >
    <span className="hidden lg:block lg:text-sm 2xl:text-base">
      Registro por página
    </span>
    <Select
      disabled={isDisabled}
      defaultValue={itemsPerPage}
      value={itemsPerPage}
      onValueChange={handleItemsPerPage}
      data-testid="pagination-items-per-page-select"
    >
      <SelectTrigger
        id="items_per_page"
        className="w-20 gap-4"
        data-testid="pagination-items-per-page-select-trigger"
      >
        <SelectValue placeholder="Escolha o segmento" />
      </SelectTrigger>
      <SelectContent className="min-w-20">
        <SelectItem
          value="50"
          data-testid="pagination-items-per-page-select-item-50"
        >
          50
        </SelectItem>
        <SelectItem
          value="20"
          data-testid="pagination-items-per-page-select-item-20"
        >
          20
        </SelectItem>
        <SelectItem
          value="10"
          data-testid="pagination-items-per-page-select-item-10"
        >
          10
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
)
PaginationItemsPerPage.displayName = 'PaginationItemsPerPage'

const PaginationContent = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={twMerge(
      'z-50 mr-auto flex flex-row items-center rounded-lg border border-monochromatic-300 lg:mx-auto',
      className,
    )}
    {...props}
  />
)
PaginationContent.displayName = 'PaginationContent'

type PaginationButtonProps = {
  isActive?: boolean
  middleButton?: boolean
} & ButtonProps

const PaginationButton = ({
  className,
  isActive,
  middleButton = false,
  size = 'icon',
  ...props
}: PaginationButtonProps) => (
  <Button
    className={twMerge(
      buttonVariants({ variant: isActive ? 'primary' : 'ghost', size }),
      'rounded-none',
      middleButton ? 'border-x border-x-monochromatic-300' : '',
      'disabled:cursor-not-allowed disabled:border-monochromatic-200 disabled:bg-monochromatic-200 disabled:[&_svg]:text-monochromatic-300',
      className,
    )}
    {...props}
  />
)
PaginationButton.displayName = 'PaginationButton'

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to previous page"
    className={twMerge(
      'rounded-l-lg border-r border-r-monochromatic-300',
      className,
    )}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
  </PaginationButton>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to next page"
    className={twMerge(
      'rounded-r-lg border-l border-l-monochromatic-300',
      className,
    )}
    {...props}
  >
    <ChevronRight className="h-4 w-4" />
  </PaginationButton>
)
PaginationNext.displayName = 'PaginationNext'

export {
  Pagination,
  PaginationContent,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
  PaginationCurrentPage,
  PaginationItemsPerPage,
}
